import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import '../styles/liquidGlass.css'

function getFocusable(container) {
  if (!container) return []
  return [...container.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((el) => el.getAttribute('aria-hidden') !== 'true')
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function hasFinePointer() {
  return typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

const overlayEscapeStack = []

function registerOverlay(id) {
  overlayEscapeStack.push(id)
  return () => {
    const index = overlayEscapeStack.lastIndexOf(id)
    if (index >= 0) overlayEscapeStack.splice(index, 1)
  }
}

function isTopOverlay(id) {
  return overlayEscapeStack[overlayEscapeStack.length - 1] === id
}

let scrollLockCount = 0
let scrollLockSnapshot = null

function lockDocumentScroll() {
  if (typeof document === 'undefined') return
  if (scrollLockCount === 0) {
    const root = document.documentElement
    const leaked = root.style.overflow === 'hidden' || document.body.style.overflow === 'hidden'
    scrollLockSnapshot = {
      htmlOverflow: leaked ? '' : root.style.overflow,
      htmlPaddingRight: root.style.paddingRight,
      bodyOverflow: leaked ? '' : document.body.style.overflow,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    }
    const scrollbar = Math.max(0, window.innerWidth - root.clientWidth)
    root.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    if (scrollbar > 0) {
      const current = Number.parseFloat(getComputedStyle(root).paddingRight) || 0
      root.style.paddingRight = `${current + scrollbar}px`
    }
  }
  scrollLockCount += 1
}

function unlockDocumentScroll() {
  if (scrollLockCount === 0) return
  scrollLockCount -= 1
  if (scrollLockCount > 0 || !scrollLockSnapshot) return
  const root = document.documentElement
  const snap = scrollLockSnapshot
  scrollLockSnapshot = null
  root.style.overflow = snap.htmlOverflow
  root.style.paddingRight = snap.htmlPaddingRight
  document.body.style.overflow = snap.bodyOverflow
  window.scrollTo(snap.scrollX, snap.scrollY)
}

export default function GlassOverlay({
  isOpen,
  onClose,
  closeOnBackdrop = true,
  closeOnEscape = true,
  allowClose = true,
  labelledBy,
  describedBy,
  panelClassName = '',
  rootClassName = '',
  initialFocusRef,
  returnFocusRef,
  zIndex,
  followPointer = true,
  children,
}) {
  const fallbackLabelId = useId()
  const overlayId = useId()
  const rootRef = useRef(null)
  const dimRef = useRef(null)
  const motionRef = useRef(null)
  const panelRef = useRef(null)
  const shineRef = useRef(null)
  const lockRef = useRef(false)
  const previousFocusRef = useRef(null)
  const isOpenRef = useRef(isOpen)
  const phaseRef = useRef('closed')
  const pointerTarget = useRef({ x: 52, y: 18 })
  const pointerCurrent = useRef({ x: 52, y: 18 })
  const [phase, setPhase] = useState('closed')

  isOpenRef.current = isOpen
  phaseRef.current = phase

  const settleClosed = () => {
    if (isOpenRef.current) return
    if (phaseRef.current === 'closed') return
    setPhase('closed')
    if (lockRef.current) {
      unlockDocumentScroll()
      lockRef.current = false
    }
    const restore = returnFocusRef?.current || previousFocusRef.current
    if (restore && typeof restore.focus === 'function') {
      restore.focus({ preventScroll: true })
    }
  }

  useLayoutEffect(() => {
    if (isOpen) {
      if (!lockRef.current) {
        previousFocusRef.current = document.activeElement
        lockDocumentScroll()
        lockRef.current = true
      }
      setPhase(prefersReducedMotion() ? 'open' : 'prime')
      return undefined
    }
    if (phaseRef.current === 'closed') return undefined
    if (prefersReducedMotion()) {
      settleClosed()
      return undefined
    }
    setPhase('leave')
    return undefined
  }, [isOpen])

  useEffect(() => () => {
    if (lockRef.current) {
      unlockDocumentScroll()
      lockRef.current = false
    }
  }, [])

  useLayoutEffect(() => {
    if (phase !== 'prime') return undefined
    const dim = dimRef.current
    if (dim) {
      dim.getBoundingClientRect()
      void getComputedStyle(dim).backdropFilter
    }
    let inner = 0
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        if (isOpenRef.current && phaseRef.current === 'prime') setPhase('enter')
      })
    })
    return () => {
      window.cancelAnimationFrame(outer)
      if (inner) window.cancelAnimationFrame(inner)
    }
  }, [phase])

  useLayoutEffect(() => {
    if (phase !== 'enter') return undefined
    const frame = window.requestAnimationFrame(() => {
      const running = motionRef.current?.getAnimations().some((animation) => animation.animationName === 'lg-slide-in')
      if (!running && isOpenRef.current && phaseRef.current === 'enter') setPhase('open')
    })
    return () => window.cancelAnimationFrame(frame)
  }, [phase])

  useLayoutEffect(() => {
    if (phase !== 'leave') return undefined
    const running = motionRef.current?.getAnimations().some((animation) => animation.animationName === 'lg-slide-out')
    if (!running) settleClosed()
    return undefined
  }, [phase])

  useEffect(() => {
    if (phase !== 'enter' && phase !== 'open') return undefined
    if (phase === 'enter') {
      const preferred = initialFocusRef?.current
      const fallback = getFocusable(panelRef.current)[0]
      const target = preferred && typeof preferred.focus === 'function' ? preferred : fallback
      target?.focus({ preventScroll: true })
    }
    return undefined
  }, [phase, initialFocusRef])

  useEffect(() => {
    if (phase === 'closed') return undefined
    return registerOverlay(overlayId)
  }, [phase, overlayId])

  useEffect(() => {
    if (phase === 'closed') return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (event.defaultPrevented || !isTopOverlay(overlayId)) return
        event.preventDefault()
        if (closeOnEscape && allowClose && phaseRef.current !== 'leave' && phaseRef.current !== 'prime') onClose?.()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const nodes = getFocusable(panelRef.current)
      if (nodes.length === 0) return
      const firstNode = nodes[0]
      const lastNode = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault()
        lastNode.focus()
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault()
        firstNode.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [phase, overlayId, onClose, closeOnEscape, allowClose])

  useEffect(() => {
    if (!followPointer) return undefined
    if (phase === 'closed' || phase === 'prime' || prefersReducedMotion() || !hasFinePointer()) return undefined
    const shine = shineRef.current
    if (!shine) return undefined

    let frame = 0
    const apply = () => {
      const current = pointerCurrent.current
      const target = pointerTarget.current
      const dx = target.x - current.x
      const dy = target.y - current.y
      if (Math.abs(dx) < 0.08 && Math.abs(dy) < 0.08) {
        current.x = target.x
        current.y = target.y
        shine.style.setProperty('--lg-px', current.x.toFixed(2))
        shine.style.setProperty('--lg-py', current.y.toFixed(2))
        frame = 0
        return
      }
      current.x += dx * 0.14
      current.y += dy * 0.14
      shine.style.setProperty('--lg-px', current.x.toFixed(2))
      shine.style.setProperty('--lg-py', current.y.toFixed(2))
      frame = window.requestAnimationFrame(apply)
    }
    const kick = () => {
      if (!frame) frame = window.requestAnimationFrame(apply)
    }

    const onPointerMove = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return
      const panel = panelRef.current
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      pointerTarget.current = {
        x: Math.min(108, Math.max(-8, ((event.clientX - rect.left) / rect.width) * 100)),
        y: Math.min(108, Math.max(-8, ((event.clientY - rect.top) / rect.height) * 100)),
      }
      kick()
    }

    window.addEventListener('pointermove', onPointerMove, { passive: true })
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onPointerMove)
    }
  }, [phase, followPointer])

  const onMotionAnimationEnd = (event) => {
    if (event.target !== event.currentTarget) return
    if (event.animationName === 'lg-slide-in' && isOpenRef.current) {
      setPhase('open')
      return
    }
    if (event.animationName !== 'lg-slide-out') return
    settleClosed()
  }

  const canDismiss = allowClose && phase !== 'leave' && phase !== 'prime'

  const overlay = phase === 'closed' ? null : (
    <div
      ref={rootRef}
      className={['lg-root', `is-${phase}`, rootClassName].filter(Boolean).join(' ')}
      style={zIndex ? { zIndex } : undefined}
    >
      <div
        ref={dimRef}
        className="lg-dim"
        onPointerDown={() => {
          if (closeOnBackdrop && canDismiss) onClose?.()
        }}
      />
      <div className="lg-stage">
        <div className="lg-motion" ref={motionRef} onAnimationEnd={onMotionAnimationEnd}>
          <div
            ref={panelRef}
            className={`lg-panel${panelClassName ? ` ${panelClassName}` : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy || fallbackLabelId}
            aria-describedby={describedBy}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="lg-panel-frost" aria-hidden="true" />
            <div className="lg-panel-tint" aria-hidden="true" />
            <div className="lg-panel-shine" ref={shineRef} aria-hidden="true">
              <div className="lg-panel-shine-idle" />
              <div className="lg-panel-shine-spot" />
              <div className="lg-panel-rim" />
            </div>
            <div className="lg-panel-body">{children}</div>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(
    <>
      <div className="lg-blur-prewarm" aria-hidden="true" />
      {overlay}
    </>,
    document.body,
  )
}
